import React, { useState, useEffect, useRef } from 'react'
import { Plus, Trash2, Edit, Save, X, Loader, AlertCircle, CheckCircle, Package, Link } from 'lucide-react'
import type { Product, Subprocessor } from '../lib/storage'
import SectionHeading from './SectionHeading'

interface ProductManagerProps {
  currentUser?: { email: string; role: string; name: string } | null
}

const ProductManager = ({ currentUser }: ProductManagerProps) => {
  const [products, setProducts] = useState<Product[]>([])
  const [subprocessors, setSubprocessors] = useState<Subprocessor[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ name: '', description: '', isDefault: false })
  const [newProductForm, setNewProductForm] = useState({ name: '', description: '', isDefault: false })
  const [showNewForm, setShowNewForm] = useState(false)
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null)
  const [togglingSubprocessor, setTogglingSubprocessor] = useState<string | null>(null)
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    fetchProducts()
    fetchSubprocessors()
  }, [])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch('/api/products', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        const products = data.success ? data.result.products : data
        setProducts(products || [])
      } else {
        setError('Failed to fetch products')
      }
    } catch (err) {
      console.error('[ProductManager] Error fetching products:', err)
      setError('Error fetching products')
    } finally {
      setLoading(false)
    }
  }

  const fetchSubprocessors = async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch('/api/subprocessors', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        const vendors = data.success ? data.result.vendors : data
        setSubprocessors((vendors || []).filter((s: Subprocessor) => !s.deleted && !s.archived))
      }
    } catch (err) {
      console.error('Error fetching subprocessors:', err)
    }
  }

  const toggleSubprocessorAssociation = async (productId: string, subprocessorId: string) => {
    // Prevent concurrent execution to avoid race conditions
    const toggleKey = `${productId}-${subprocessorId}`
    if (togglingSubprocessor === toggleKey) return

    try {
      setTogglingSubprocessor(toggleKey)

      const subprocessor = subprocessors.find(s => s.id === subprocessorId)
      if (!subprocessor) return

      const currentProductIds = subprocessor.productIds || []
      const newProductIds = currentProductIds.includes(productId)
        ? currentProductIds.filter(id => id !== productId)
        : [...currentProductIds, productId]

      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch('/api/subprocessors', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          itemId: subprocessorId,
          item: {
            ...subprocessor,
            productIds: newProductIds
          },
          adminEmail: currentUser?.email || 'admin@system'
        })
      })

      if (response.ok) {
        await fetchSubprocessors()
        setSuccess('Subprocessor association updated')
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current)
        }
        successTimeoutRef.current = setTimeout(() => {
          setSuccess('')
          successTimeoutRef.current = null
        }, 2000)
      } else {
        setError('Failed to update subprocessor association')
      }
    } catch (err) {
      console.error('[ProductManager] Error updating subprocessor association:', err)
      setError('Error updating subprocessor association')
    } finally {
      // Re-enable after full operation completes
      setTogglingSubprocessor(null)
    }
  }

  const handleSave = async (productId: string | null, formData: any) => {
    try {
      setLoading(true)
      setError('')
      setSuccess('')

      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const url = productId
        ? `/api/products/${productId}`
        : '/api/products'

      const response = await fetch(url, {
        method: productId ? 'PUT' : 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setSuccess(productId ? 'Product updated successfully' : 'Product created successfully')
        setEditingId(null)
        setShowNewForm(false)
        setNewProductForm({ name: '', description: '', isDefault: false })
        await fetchProducts()
      } else {
        const data = await response.json()
        setError(data.message || 'Failed to save product')
      }
    } catch (err) {
      setError('Error saving product')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this product? It will be removed from all associated subprocessors.')) return

    try {
      setLoading(true)
      setError('')
      setSuccess('')

      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(`/api/products/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        setSuccess('Product deleted successfully')
        await fetchProducts()
      } else {
        const data = await response.json()
        setError(data.message || 'Failed to delete product')
      }
    } catch (err) {
      setError('Error deleting product')
    } finally {
      setLoading(false)
    }
  }

  const startEdit = (product: Product) => {
    setEditingId(product.id)
    setEditForm({
      name: product.name,
      description: product.description,
      isDefault: product.isDefault
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <SectionHeading id="products" className="text-2xl font-bold text-gray-900">
            Product Management
          </SectionHeading>
          <p className="mt-2 text-gray-600">
            Manage your products/services and associate subprocessors with each one.
          </p>
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Product
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-red-600">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-600">{success}</span>
        </div>
      )}

      {/* New Product Form */}
      {showNewForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">New Product</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
              <input
                type="text"
                value={newProductForm.name}
                onChange={(e) => setNewProductForm({ ...newProductForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Platform, Analytics, Mobile App"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newProductForm.description}
                onChange={(e) => setNewProductForm({ ...newProductForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Brief description of this product or service"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={newProductForm.isDefault}
                onChange={(e) => setNewProductForm({ ...newProductForm, isDefault: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700">
                Set as default product (subprocessors will use this if no product is specified)
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleSave(null, newProductForm)}
                disabled={loading || !newProductForm.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </button>
              <button
                onClick={() => {
                  setShowNewForm(false)
                  setNewProductForm({ name: '', description: '', isDefault: false })
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Products List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Products ({products.length})
          </h3>
          {products.length === 0 ? (
            <p className="text-gray-500">No products defined. Add your first product to get started.</p>
          ) : (
            <div className="space-y-3">
              {products.map((product) => (
                <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                  {editingId === product.id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={2}
                      />
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.isDefault}
                          onChange={(e) => setEditForm({ ...editForm, isDefault: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">Set as default product</label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(product.id, editForm)}
                          disabled={loading}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center text-sm"
                        >
                          <Save className="h-3 w-3 mr-1" />
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center text-sm"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Package className="h-5 w-5 text-blue-600" />
                          <h4 className="font-semibold text-gray-900">{product.name}</h4>
                          {product.isDefault && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                              Default
                            </span>
                          )}
                        </div>
                        {product.description && (
                          <p className="text-sm text-gray-600 mt-1 ml-7">{product.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 ml-7">
                          <p className="text-xs text-gray-500">
                            Updated {new Date(product.updatedAt).toLocaleDateString()} by {product.updatedBy}
                          </p>
                          <button
                            onClick={() => setExpandedProductId(expandedProductId === product.id ? null : product.id)}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <Link className="h-3 w-3" />
                            {subprocessors.filter(s => s.productIds?.includes(product.id)).length} subprocessors
                          </button>
                        </div>
                        {expandedProductId === product.id && (
                          <div className="mt-3 ml-7 border-l-2 border-blue-200 pl-4">
                            <h5 className="text-xs font-semibold text-gray-700 mb-2">Associated Subprocessors</h5>
                            {subprocessors.length === 0 ? (
                              <p className="text-xs text-gray-500">No subprocessors available. Add subprocessors in the Content tab.</p>
                            ) : (
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {subprocessors.map(sub => {
                                  const isAssociated = (sub.productIds || []).includes(product.id)
                                  const toggleKey = `${product.id}-${sub.id}`
                                  const isToggling = togglingSubprocessor === toggleKey
                                  return (
                                    <label key={sub.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                      <input
                                        type="checkbox"
                                        checked={isAssociated}
                                        disabled={isToggling}
                                        onChange={() => toggleSubprocessorAssociation(product.id, sub.id)}
                                        className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                      />
                                      <span className="text-xs text-gray-700">{sub.name}</span>
                                      <span className="text-xs text-gray-500">({sub.type})</span>
                                    </label>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(product)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          disabled={loading || product.isDefault}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          title={product.isDefault ? 'Cannot delete default product' : 'Delete product'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <Loader className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      )}
    </div>
  )
}

export default ProductManager
